import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import styles from './IdentityExist.module.scss'
import AccountForm from '../../types/account-form.types'
import { State } from '../../state-actions-reducers'
import useDebounce from "../../utils/useDebounce";
import { IdentityCardDtoModel, searchByIdentity } from "../../hooks/useSearchByIdentityHook";
import { convToInt } from '../../utils/helpers'
import { getSavedFormId } from '../../local-storage'
import { Lang } from '../../types/common-types'
import TgIcon from '../../images/telegram.svg'
import WhatsAppIcon from '../../images/whatsapp.svg'
import PhoneIcon from '../../images/phone.svg'
import { getCommonContent, Services_Common_Dto_DT_Public_PublicOrganizationDto, Services_Common_Enums_SocialLinkType } from '../../hooks/useGetContactsCustomHook';

interface Props {
  accountForm: AccountForm
  lang: Lang
  show: boolean
}

export function splitPassportNumber(input: string): [string, string] {
  // 4313-343 434
  input = input.replace(/[^\d]/g, ''); // Удаляем все символы, кроме цифр
  // 4313343 434
  input = input.replace(' ', ''); // Удаляем все символы, кроме цифр
  // 4313343434
  input = input.substring(0, 2) + ' ' + input.substring(2);
  // 43 13343434
  const firstPart = input.substring(0, 5);
  // 43 13
  const secondPart = input.substring(input.length - 6);
  // 343434
  return [firstPart, secondPart];
}

function IdentityExist({
  accountForm,
  lang,
  show
}: Props) {
  const { watch, setValue } = useFormContext<State>()
  const state = watch()

  const debouncePassportType = useDebounce(state.passportType, 1500)
  const debouncePassportSeries = useDebounce(state.passportSeries, 1500)
  const debouncePassportNumber = useDebounce(state.passportNumber, 1500)
  const debouncePassportIAt = useDebounce(state.passportIAt, 1500)
  const debouncePassportIssuedBy = useDebounce(state.passportIssuedBy, 1500)
  const debouncePassportAuthorityCode = useDebounce(state.passportAuthorityCode, 1500)
  const debouncePassportPersonNumber = useDebounce(state.passportPersonNumber, 1500)
  const debouncePassportFirstNationality = useDebounce(state.passportFirstNationality, 1500)
  const debouncePassportCardNumber = useDebounce(state.passportCardNumber, 1500)
  const debouncePassportExpiryDate = useDebounce(state.passportExpiryDate, 1500)
  const debouncePassportIsPermanentResidency = useDebounce(state.passportIsPermanentResidency, 1500)
  const debouncePassportDateIssuePermanentResidency = useDebounce(state.passportDateIssuePermanentResidency, 1500)
  const debouncePassportPermanentResidencyNumber = useDebounce(state.passportPermanentResidencyNumber, 1500)
  const debouncePassportInternationalNumber = useDebounce(state.passportInternationalNumber, 1500)
  const debouncePassportDateBoardCrossing = useDebounce(state.passportDateBoardCrossing, 1500)

  const [passportSeries, passportNumber] = splitPassportNumber(debouncePassportNumber)

  const identity: IdentityCardDtoModel = useMemo(() => ({
    identityType: convToInt(debouncePassportType) ?? undefined,
    series: state.citizenship === "643" ? passportSeries.replace(' ', '') : debouncePassportSeries.replace(' ', ''),
    number: state.citizenship === "643" ? passportNumber.replace(' ', '') : debouncePassportNumber.replace(' ', ''),
    dateIssue: debouncePassportIAt,
    issuedBy: debouncePassportIssuedBy,
    authorityCode: debouncePassportAuthorityCode,
    personNumber: debouncePassportPersonNumber.replace(' ', ''),
    firstNationality: debouncePassportFirstNationality,
    identificationCardNumber: debouncePassportCardNumber.replace(' ', ''),
    dateExpiry: debouncePassportExpiryDate,
    isPermanentResidency: debouncePassportIsPermanentResidency,
    dateIssuePermanentResidency: debouncePassportDateIssuePermanentResidency,
    permanentResidencyNumber: debouncePassportPermanentResidencyNumber.replace(' ', ''),
    internationalPassportNumber: debouncePassportInternationalNumber.replace(' ', ''),
    dateBoardCrossing: debouncePassportDateBoardCrossing,
    citizenship: state.citizenship
  }),
    [debouncePassportType, debouncePassportSeries, debouncePassportNumber, debouncePassportIAt, debouncePassportIssuedBy,
      debouncePassportAuthorityCode, debouncePassportPersonNumber, debouncePassportFirstNationality, debouncePassportCardNumber,
      debouncePassportExpiryDate, debouncePassportIsPermanentResidency, debouncePassportDateIssuePermanentResidency,
      debouncePassportPermanentResidencyNumber, debouncePassportInternationalNumber, debouncePassportDateBoardCrossing, state.formId]);

  function handleServerError() {
    setValue('serverError', true, { shouldDirty: true });
    alert(accountForm.serverError[lang]);
  }

  const onIdentityChange = async () => {
    try {
      const response = await searchByIdentity(state, identity);
      if (response.message !== 'Invalid token') {
        setValue('serverError', false, { shouldDirty: true });
        setValue('isIdentityOk', response.isIdentityOk, { shouldDirty: true });
      } else {
        handleServerError()
      }
    } catch (error) {
      handleServerError()
      console.error('Error in onIdentityChange:', error);
    }
  };

  useEffect(() => {
    const formId = getSavedFormId(window.localStorage)
    console.log('formId', formId)
    formId && onIdentityChange()
  }, [identity]);

  const [contacts, setContacts] = useState<Services_Common_Dto_DT_Public_PublicOrganizationDto>()

  async function fetchContacts() {
    const response = await getCommonContent()
    setContacts(response)
  }

  useEffect(() => {
    fetchContacts();
  }, [])

  const phoneLink = useMemo(() => {
    return contacts?.phoneNumberPrimary
  }, [contacts])

  const tgLink = useMemo(() => {
    return contacts?.socialLinks?.find((e) => e.type === Services_Common_Enums_SocialLinkType.Telegram)
  }, [contacts])

  const whatsAppLink = useMemo(() => {
    return contacts?.socialLinks?.find((e) => e.type === Services_Common_Enums_SocialLinkType.Whatsapp)
  }, [contacts])

  return (
    show ? <div className="col-12">
      <div className={styles.identityExistContainer}>
        <span>{accountForm.identityExistHead[lang]}</span>
        <p>{accountForm.identityExistDesc[lang]}</p>
        <div className={styles.links}>
          {phoneLink && <a href={`tel:${phoneLink}`}>
            <img src={PhoneIcon} alt={'phone'}></img>
            {accountForm.phoneCallback[lang]}
          </a>}
          {tgLink?.link && <a href={tgLink?.link} target="_blank">
            <img src={TgIcon} alt={'tg'}></img>
            {accountForm.tgCallback[lang]}
          </a>}
          {whatsAppLink?.link && <a href={whatsAppLink?.link} target="_blank">
            <img src={WhatsAppIcon} alt={'whatsapp'}></img>
            {accountForm.whatsAppCallback[lang]}
          </a>}
        </div>
      </div>
    </div> : null
  )
}

export default IdentityExist
